import React from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { Page } from '../../../types';
import { ImageMetadata } from '../../../services/imageService';
import { HtmlCssEditTab } from './HtmlCssEditTab';
import { OtherSettingsTab } from './OtherSettingsTab';
import { HorizontalSlideTab } from '../visualEditor/tabs/HorizontalSlideTab';
import { PagePreview } from './PagePreview';
import { RightDrawer, ConfirmDialog } from '../common';

interface PageEditDialogProps {
  open: boolean;
  onClose: () => void;
  page: Page | null;
  onSave: (pageData: Partial<Page>) => void;
  siteId?: string;
  editState?: any;
  updateEditState?: (updates: any) => void;
  updateSite?: (siteId: string, siteData: Partial<any>) => Promise<void>;
  updateAppliedContent?: (pageId: string, html: string, css: string) => void;
  getAppliedContent?: (pageId: string) => {html: string, css: string};
  cachedImages?: ImageMetadata[];
  preloaded?: boolean;
  onPendingSlideImageFile?: (slideKey: string, file: File | null) => void;
}

export const PageEditDialog: React.FC<PageEditDialogProps> = ({
  open,
  onClose,
  page,
  onSave,
  siteId,
  editState,
  updateEditState,
  updateSite,
  updateAppliedContent,
  getAppliedContent,
  cachedImages = [],
  preloaded = false,
  onPendingSlideImageFile,
}) => {
  const [menuValue, setMenuValue] = React.useState(0); // 0: 横スライド, 1: HTML/CSS, 2: その他
  const [codeContent, setCodeContent] = React.useState('');
  const [savedCodeContent, setSavedCodeContent] = React.useState('');
  const [ctaButtonVisible, setCtaButtonVisible] = React.useState<boolean>(() => {
    if (page) {
      const buttonIds = page.ctaButtonIds || [];
      const buttonId = page.ctaButtonId || '';
      return buttonIds.length > 0 || !!buttonId;
    }
    return true;
  });
  const [ctaButtonId, setCtaButtonId] = React.useState<string>(page?.ctaButtonId || ''); // 後方互換性のため残す
  const [ctaButtonIds, setCtaButtonIds] = React.useState<string[]>(page?.ctaButtonIds || []); // 複数選択用
  const [companyInfoVisible, setCompanyInfoVisible] = React.useState<boolean>(() => {
    if (page && page.companyInfoVisible !== undefined) {
      return page.companyInfoVisible;
    }
    return false;
  });
  const [horizontalSlideData, setHorizontalSlideData] = React.useState<Partial<Page>>({});
  const [initialized, setInitialized] = React.useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false);
  const initialValuesRef = React.useRef<{
    codeContent: string;
    ctaButtonVisible: boolean;
    ctaButtonId: string;
    ctaButtonIds: string[];
    companyInfoVisible: boolean;
    horizontalSlideData: Partial<Page>;
  } | null>(null);
  const pageIdRef = React.useRef<string | null>(null);

  // スロットIDを取得（例: A-1）
  const slotId = page ? `${page.column}-${page.row}` : '';

  // 初期化処理
  React.useEffect(() => {
    if (open && page) {
      // pageのIDが変更された場合は初期化をリセット
      if (pageIdRef.current !== page.id) {
        setInitialized(false);
        pageIdRef.current = page.id;
      }
      
      if (!initialized) {
        const existingCode = page.customHtml || '';
        let initialCode = existingCode;
        setCodeContent(existingCode);
        setSavedCodeContent(existingCode);
        
        if (getAppliedContent && getAppliedContent(page.id).html) {
          const applied = getAppliedContent(page.id);
          initialCode = applied.html;
          setCodeContent(applied.html);
          setSavedCodeContent(applied.html);
        }
        
        // CTAボタンの初期化（複数選択対応）
        const buttonIds = page.ctaButtonIds || [];
        const buttonId = page.ctaButtonId || '';
        const initialCtaButtonVisible = buttonIds.length > 0 || buttonId !== '';
        const initialCompanyInfoVisible = page.companyInfoVisible !== undefined ? page.companyInfoVisible : false;
        
        setCtaButtonIds(buttonIds);
        setCtaButtonId(buttonId);
        setCtaButtonVisible(initialCtaButtonVisible);
        setCompanyInfoVisible(initialCompanyInfoVisible);
        
        // 横スライドデータの初期化
        const initialSlideData = {
          horizontalSlides: page.horizontalSlides,
          horizontalSlideWidth: page.horizontalSlideWidth,
          horizontalSlideViewportWidth: page.horizontalSlideViewportWidth,
          horizontalSlideIndicatorPosition: page.horizontalSlideIndicatorPosition,
          horizontalSlideInfiniteLoop: page.horizontalSlideInfiniteLoop,
          horizontalSlideInitialIndex: page.horizontalSlideInitialIndex,
          horizontalSlidePositionAdjustment: page.horizontalSlidePositionAdjustment,
        };
        setHorizontalSlideData(initialSlideData);
        
        // 初期値を保存
        initialValuesRef.current = {
          codeContent: initialCode,
          ctaButtonVisible: initialCtaButtonVisible,
          ctaButtonId: buttonId,
          ctaButtonIds: buttonIds,
          companyInfoVisible: initialCompanyInfoVisible,
          horizontalSlideData: initialSlideData,
        };
        
        setInitialized(true);
      }
    } else if (!open) {
      setInitialized(false);
      setHorizontalSlideData({});
      initialValuesRef.current = null;
      pageIdRef.current = null;
    }
  }, [open, page, getAppliedContent, initialized]);

  const handleSave = () => {
    if (page && updateAppliedContent) {
      const customCode = codeContent;
      updateAppliedContent(page.id, customCode, '');
      
      const pageData = {
        customHtml: customCode,
        customCss: '',
        ctaButtonVisible: ctaButtonVisible,
        ctaButtonId: ctaButtonId,
        ctaButtonIds: ctaButtonIds,
        companyInfoVisible: companyInfoVisible,
        // 横スライドデータは状態から取得
        ...(horizontalSlideData.horizontalSlides && { horizontalSlides: horizontalSlideData.horizontalSlides }),
        ...(horizontalSlideData.horizontalSlideWidth !== undefined && { horizontalSlideWidth: horizontalSlideData.horizontalSlideWidth }),
        ...(horizontalSlideData.horizontalSlideViewportWidth !== undefined && { horizontalSlideViewportWidth: horizontalSlideData.horizontalSlideViewportWidth }),
        ...(horizontalSlideData.horizontalSlideIndicatorPosition && { horizontalSlideIndicatorPosition: horizontalSlideData.horizontalSlideIndicatorPosition }),
        ...(horizontalSlideData.horizontalSlideInfiniteLoop !== undefined && { horizontalSlideInfiniteLoop: horizontalSlideData.horizontalSlideInfiniteLoop }),
        ...(horizontalSlideData.horizontalSlideInitialIndex !== undefined && { horizontalSlideInitialIndex: horizontalSlideData.horizontalSlideInitialIndex }),
        ...(horizontalSlideData.horizontalSlidePositionAdjustment !== undefined && { horizontalSlidePositionAdjustment: horizontalSlideData.horizontalSlidePositionAdjustment })
      };
      
      onSave(pageData);
      setSavedCodeContent(customCode);
      
      // 保存時にinitialValuesRefを更新（次回開いた時に保存された値が初期値として使われる）
      if (initialValuesRef.current) {
        initialValuesRef.current = {
          codeContent: customCode,
          ctaButtonVisible: ctaButtonVisible,
          ctaButtonId: ctaButtonId,
          ctaButtonIds: ctaButtonIds,
          companyInfoVisible: companyInfoVisible,
          horizontalSlideData: horizontalSlideData,
        };
      }
    }
  };

  const handleCancel = () => {
    setConfirmCloseOpen(true);
  };

  const hasChanges = (): boolean => {
    if (!initialValuesRef.current || !page) return false;
    const initial = initialValuesRef.current;
    
    // codeContentの変更チェック
    if (codeContent !== initial.codeContent) return true;
    
    // CTAボタン関連の変更チェック
    if (ctaButtonVisible !== initial.ctaButtonVisible) return true;
    if (ctaButtonId !== initial.ctaButtonId) return true;
    if (JSON.stringify(ctaButtonIds.sort()) !== JSON.stringify(initial.ctaButtonIds.sort())) return true;
    
    // companyInfoVisibleの変更チェック
    if (companyInfoVisible !== initial.companyInfoVisible) return true;
    
    // 横スライドデータの変更チェック（各プロパティを個別に比較）
    const currentSlideData = horizontalSlideData;
    const initialSlideData = initial.horizontalSlideData;
    
    if (currentSlideData.horizontalSlideWidth !== initialSlideData.horizontalSlideWidth) return true;
    if (currentSlideData.horizontalSlideViewportWidth !== initialSlideData.horizontalSlideViewportWidth) return true;
    if (currentSlideData.horizontalSlideIndicatorPosition !== initialSlideData.horizontalSlideIndicatorPosition) return true;
    if (currentSlideData.horizontalSlideInfiniteLoop !== initialSlideData.horizontalSlideInfiniteLoop) return true;
    if (currentSlideData.horizontalSlideInitialIndex !== initialSlideData.horizontalSlideInitialIndex) return true;
    if (currentSlideData.horizontalSlidePositionAdjustment !== initialSlideData.horizontalSlidePositionAdjustment) return true;
    
    // horizontalSlidesの比較
    const currentSlides = currentSlideData.horizontalSlides || [];
    const initialSlides = initialSlideData.horizontalSlides || [];
    if (currentSlides.length !== initialSlides.length) return true;
    if (JSON.stringify(currentSlides) !== JSON.stringify(initialSlides)) return true;
    
    return false;
  };

  const handleCloseRequest = () => {
    if (hasChanges()) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleCloseConfirm = () => {
    if (initialValuesRef.current) {
      const initial = initialValuesRef.current;
      
      // 全ての状態を初期値にリセット
      setCodeContent(initial.codeContent);
      setSavedCodeContent(initial.codeContent);
      setCtaButtonId(initial.ctaButtonId);
      setCtaButtonIds(initial.ctaButtonIds);
      setCtaButtonVisible(initial.ctaButtonVisible);
      setCompanyInfoVisible(initial.companyInfoVisible);
      setHorizontalSlideData(initial.horizontalSlideData);
    }
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleCloseCancel = () => {
    setConfirmCloseOpen(false);
  };

  const handleChangeSlideData = React.useCallback((slideData: Partial<Page>) => {
    // 状態を更新（pageオブジェクトへの直接変更は行わない）
    setHorizontalSlideData(prevData => ({
      ...prevData,
      ...slideData
    }));
  }, []);

  if (!page) return null;

  return (
    <>
    <RightDrawer
      open={open} 
      onClose={handleCloseRequest}
      width={{ xs: '100%', sm: '1050px' }}
    >
      <Box sx={{ height: '100%', display: 'flex', backgroundColor: '#F7F7F7' }}>
        {/* 左側：メニュー */}
        <Box 
          sx={{ 
            width: '200px', 
            backgroundColor: '#F0F0F0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* タイトル */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
            <SettingsIcon sx={{ fontSize: '20px', color: '#666666' }} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#666666' }}>
              ページ設定
            </Typography>
          </Box>

          <List sx={{ pt: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                selected={menuValue === 0}
                onClick={() => setMenuValue(0)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#FFFFFF',
                    color: '#1976D2',
                    borderRight: '4px solid #1976D2',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#E8E8E8',
                  },
                }}
              >
                <ListItemText primary="横スライド" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={menuValue === 1}
                onClick={() => setMenuValue(1)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#FFFFFF',
                    color: '#1976D2',
                    borderRight: '4px solid #1976D2',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#E8E8E8',
                  },
                }}
              >
                <ListItemText primary="HTML / CSS" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={menuValue === 2}
                onClick={() => setMenuValue(2)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#FFFFFF',
                    color: '#1976D2',
                    borderRight: '4px solid #1976D2',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#E8E8E8',
                  },
                }}
              >
                <ListItemText primary="その他" />
              </ListItemButton>
            </ListItem>
          </List>
      </Box>

        {/* 右側：コンテンツエリア */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#F7F7F7' }}>
          {/* ヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleCloseRequest}
                sx={{ 
                  padding: '8px',
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                ページ設定
              </Typography>
              {slotId && (
                <Typography variant="body2" sx={{ ml: 2, color: '#666666' }}>
                  編集 : {slotId}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseRequest}
              sx={{
                padding: '8px',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* コンテンツ */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            {/* 左側：設定エリア（スクロール可能） */}
            <Box 
            sx={{ 
              width: '500px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              overflowY: 'auto',
              pt: 2,
              px: 3,
              pb: 3,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {/* 横スライド設定 */}
            {menuValue === 0 && (
          <HorizontalSlideTab
            key={`${page?.id}-${open}`}
            page={page}
            onSave={(slideData) => {
              const updatedPage = {
                ...page,
                ...slideData
              };
              onSave(updatedPage);
            }}
            siteId={siteId}
            editState={editState}
            updateEditState={updateEditState}
            cachedImages={cachedImages}
            preloaded={preloaded}
            onChangeSlideData={handleChangeSlideData}
            onPendingSlideImageFile={onPendingSlideImageFile}
          />
        )}
            
            {/* HTML/CSS設定 */}
            {menuValue === 1 && (
              <HtmlCssEditTab
                page={page}
                codeContent={codeContent}
                onCodeContentChange={setCodeContent}
                ctaButtonIds={ctaButtonIds}
                editState={editState}
              />
            )}
            
            {/* その他設定 */}
            {menuValue === 2 && (
              <OtherSettingsTab
                page={page}
                ctaButtonVisible={ctaButtonVisible}
                ctaButtonId={ctaButtonId}
                ctaButtonIds={ctaButtonIds}
                companyInfoVisible={companyInfoVisible}
                onCtaButtonVisibleChange={setCtaButtonVisible}
                onCtaButtonIdChange={setCtaButtonId}
                onCtaButtonIdsChange={setCtaButtonIds}
                onCompanyInfoVisibleChange={setCompanyInfoVisible}
                editState={editState}
              />
            )}
            </Box>

            {/* 右側：プレビューエリアとボタン */}
            <Box
            sx={{ 
              width: '350px',
              display: 'flex', 
              flexDirection: 'column',
              backgroundColor: '#F7F7F7'
            }}
          >
            {/* プレビューエリア */}
            <Box 
              sx={{ 
                flex: 1,
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'flex-start',
                p: 2,
                minHeight: 0,
                pt: 3,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <PagePreview
                page={page}
                codeContent={codeContent}
                previewType={menuValue === 1 ? 'html' : 'settings'}
                ctaButtonVisible={ctaButtonVisible}
                ctaButtonId={ctaButtonId}
                ctaButtonIds={ctaButtonIds}
                companyInfoVisible={companyInfoVisible}
                editState={editState}
                horizontalSlideData={horizontalSlideData}
              />
            </Box>

            {/* フッター（ボタン） */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 3 }}>
              <Button 
                onClick={handleCancel}
                variant="contained"
                sx={{
                  minWidth: '120px',
                  height: '48px',
                  borderRadius: '8px',
                  backgroundColor: '#9E9E9E',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#757575',
                  },
                }}
              >
                編集中止
        </Button>
              <Button 
                onClick={handleSave}
                variant="contained"
                startIcon={<CheckIcon />}
                sx={{
                  minWidth: '120px',
                  height: '48px',
                  borderRadius: '8px',
                  backgroundColor: '#F49121',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#E0821A',
                  },
                }}
              >
          保存
        </Button>
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>
    </RightDrawer>

    {/* 編集中止確認ダイアログ */}
    <ConfirmDialog
      open={confirmCloseOpen}
      onClose={handleCloseCancel}
      onConfirm={handleCloseConfirm}
      message={
        <>
          編集を中止します。よろしいですか?
          <br />
          <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 1 }}>
            ※編集中のデータは保存されません
          </Typography>
        </>
      }
      confirmText="編集中止"
      confirmColor="#9E9E9E"
      confirmHoverColor="#757575"
      reverseButtons={false}
    />
    </>
  );
};
